/**
 * SRA003-514: plain-language banner stating which yield path a calc used and
 * why, shown at the top of the calc result. Driven by the BE `yield_source` /
 * `reason` contract; falls back to the SRA003-475 `ml_fallback_reason` notice
 * when the BE has not yet shipped `yield_source` (so dev stays correct until
 * the -514 BE PR lands).
 *
 * Pure function shared by ContractNegotiationGrowerComponent and
 * GroupGrowerBlockComponent, which previously each carried a byte-identical
 * copy of this switch.
 */
export interface YieldSourceBanner {
  text: string;
  tone: 'info' | 'warn';
}

export function yieldSourceBanner(
  yieldSource: string | null,
  yieldReason: string | null,
  mlFallbackReason: string | null,
): YieldSourceBanner | null {
  switch (yieldSource) {
    case 'ml':
      return { text: 'Yield estimated using the ML model.', tone: 'info' };
    case 'legacy':
      switch (yieldReason) {
        case 'no_row_type':
          return {
            text: 'Legacy yield calculation used — no row type specified for this harvester.',
            tone: 'warn',
          };
        case 'ml_unavailable':
          return {
            text: 'Legacy yield calculation used — ML model temporarily unavailable.',
            tone: 'warn',
          };
        case 'unsupported_level':
          // Keep the precise BE-provided ml_fallback_reason text.
          return {
            text: mlFallbackReason ?? 'Legacy yield calculation used.',
            tone: 'warn',
          };
        default:
          // SRA003-514: legacy with no reason code. On prod USE_ML_YIELD=false
          // the BE emits yield_source="legacy" + reason=null on every calc —
          // that's the normal config mode, not a degradation, so show NO
          // banner. Only warn when a precise ml_fallback_reason (SRA003-475)
          // explains a genuine fallback.
          return mlFallbackReason
            ? { text: mlFallbackReason, tone: 'warn' }
            : null;
      }
    default:
      // Pre-514 BE: no yield_source field. Preserve -475 behaviour exactly.
      return mlFallbackReason
        ? { text: mlFallbackReason, tone: 'warn' }
        : null;
  }
}
